<script setup lang="ts">
import { computed } from 'vue'

/**
 * Text input / textarea control. v-model'able; `multiline` renders a <textarea>. `invalid` flips
 * to the danger border/ring. inheritAttrs is off so id/rows/maxlength/aria/etc. fall through to the
 * real control. Consolidates the ad-hoc form inputs onto the rounded-xl / gray / primary-focus standard.
 */
interface Props {
  modelValue?: string
  type?: string
  multiline?: boolean
  invalid?: boolean
  disabled?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  type: 'text',
  multiline: false,
  invalid: false,
  disabled: false,
})
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()
defineOptions({ inheritAttrs: false })

const onInput = (e: Event) =>
  emit('update:modelValue', (e.target as HTMLInputElement | HTMLTextAreaElement).value)

const cls = computed(() => [
  'w-full rounded-xl border bg-white px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-100',
  props.invalid
    ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-200'
    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-200',
])
</script>

<template>
  <textarea
    v-if="props.multiline"
    v-bind="$attrs"
    :value="props.modelValue"
    :disabled="props.disabled"
    :class="cls"
    @input="onInput"
  />
  <input
    v-else
    v-bind="$attrs"
    :value="props.modelValue"
    :type="props.type"
    :disabled="props.disabled"
    :class="cls"
    @input="onInput"
  />
</template>
